import React, { useState, useRef, useEffect } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const BuscadorNuevo = () => {
    const [searchInput, setSearchInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [debugInfo, setDebugInfo] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isLoadingAI, setIsLoadingAI] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const debounceRef = useRef();
    const containerRef = useRef();

    // FUNCION DE LIMPIEZA COMPLETA
    const limpiarTodo = () => {
        setSearchInput('');
        setShowSuggestions(false);
        setSuggestions([]);
        setIsLoadingAI(false);
        setIsSearching(false);
        setDebugInfo('');
        clearTimeout(debounceRef.current);
    };

    // EFECTO QUE SE EJECUTA CUANDO CAMBIA LA RUTA
    useEffect(() => {
        limpiarTodo();
    }, [location.pathname]);

    // FUNCIÓN CON EFECTO IA
    const getSuggestions = async (query) => {
        if (query.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            setIsLoadingAI(false);
            setDebugInfo('');
            return;
        }

        setIsLoadingAI(true);
        setShowSuggestions(true); //  MOSTRAR DROPDOWN INMEDIATAMENTE
        setDebugInfo(`Neural AI procesando: "${query}"...`);

        try {
            const API_BASE = process.env.REACT_APP_API_PRODUCTOS || 'http://localhost:5001';
            
            // ✅ DELAY MÍNIMO PARA VER EL SPINNER
            const [response] = await Promise.all([
                axios.get(`${API_BASE}/autocomplete`, {
                    params: {
                        q: query,
                        limit: 8
                    },
                    timeout: 5000
                }),
                new Promise(resolve => setTimeout(resolve, 800)) // ✅ Mínimo 800ms para ver el spinner
            ]);
            
            console.log('📦 Data:', response.data);
            
            setSuggestions(response.data.suggestions || []);
            setShowSuggestions((response.data.suggestions || []).length > 0);
            setDebugInfo(`AI Neural Network encontró: ${(response.data.suggestions || []).length} resultados`);
            
        } catch (error) {
            console.error('❌ Error completo:', error);
            setSuggestions([]);
            setShowSuggestions(false);
            setDebugInfo(`❌ Error: ${error.message}`);
        } finally {
            setIsLoadingAI(false);
        }
    };

    // SELECTSUGGESTION CORREGIDO (SOLO UNA FUNCIÓN)
    const selectSuggestion = async (suggestion) => {
        try {
            // Limpiar ANTES de hacer cualquier cosa
            setSearchInput('');
            setShowSuggestions(false);
            setSuggestions([]);
            setIsLoadingAI(false);
            setIsSearching(true);
            
            setDebugInfo('AI buscando producto...');
            
            const API_BASE = process.env.REACT_APP_API_PRODUCTOS || 'http://localhost:5001';
            const response = await axios.get(`${API_BASE}/busqueda_new`, {
                params: { 
                    busqueda: suggestion,
                    limit: 1
                },
                timeout: 5000
            });

            if (response.data.articulos && response.data.articulos.length > 0) {
                const primerArticulo = response.data.articulos[0];
                navigate(`/detalle?sku=${primerArticulo.Alias}`);
            } else {
                navigate(`/productos/${encodeURIComponent(suggestion)}`);
            }
            
        } catch (error) {
            console.error('❌ Error buscando producto:', error);
            navigate(`/productos/${encodeURIComponent(suggestion)}`);
        } finally {
            setIsSearching(false);
        }
    };

    // HANDLESUBMIT CORREGIDO
    // MODIFICAR handleSearchSubmit para bloquear AI después
    const handleSearchSubmit = (e) => {
        if (e.key === 'Enter' || e.type === 'click') {
            e.preventDefault();
            if (searchInput.trim().length > 0) {
                const searchTerm = searchInput;
                
                // BLOQUEAR AI POR UN TIEMPO
                setIsSearching(true); // Esto evita que getSuggestions se ejecute
                limpiarTodo();
                
                navigate(`/productos/${encodeURIComponent(searchTerm)}`);
                
                // DESBLOQUEAR DESPUÉS DE 2 SEGUNDOS
                setTimeout(() => {
                    setIsSearching(false);
                }, 2000);
            }
        }
    };

    // HANDLEINPUTCHANGE AGREGADO
    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        
        if (value.length === 0) {
            setShowSuggestions(false);
            setSuggestions([]);
            setIsLoadingAI(false);
            setDebugInfo('');
        }
    };

    // USEEFFECT PARA DEBOUNCE
    useEffect(() => {
        clearTimeout(debounceRef.current);
        
        if (searchInput.length >= 2 && !isSearching) {
            debounceRef.current = setTimeout(() => {
                getSuggestions(searchInput);
            }, 500); 
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
            setIsLoadingAI(false);
            setDebugInfo('');
        }

        return () => clearTimeout(debounceRef.current);
    }, [searchInput, isSearching]);

    // EFECTO PARA CERRAR AL HACER CLICK AFUERA
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowSuggestions(false);
                setSuggestions([]);
                setIsLoadingAI(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <Form>
                <InputGroup>
                    <Form.Control
                        type="text"
                        placeholder="Buscar con IA..."
                        value={searchInput}
                        onChange={handleInputChange}
                        onKeyDown={handleSearchSubmit}
                        autoComplete="off"
                    />
                    <Button variant="outline-secondary" onClick={handleSearchSubmit}>
                        <i className="bi bi-search"></i> Buscar
                    </Button>
                </InputGroup>
            </Form>
            
          
            {(showSuggestions || isLoadingAI) && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ced4da',
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                    
                    {/* EFECTO NEURAL AI LOADING */}
                    {isLoadingAI && (
                        <div style={{
                            padding: '20px', // ✅ Más padding
                            textAlign: 'center',
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' // ✅ Fondo con gradiente
                        }}>
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '12px', // ✅ Más espacio
                                color: '#0077b6'
                            }}>
                                {/* ✅ SPINNER DOBLE MÁS VISIBLE */}
                                <div style={{
                                    position: 'relative',
                                    width: '24px', // ✅ Más grande
                                    height: '24px'
                                }}>
                                    <div style={{
                                        position: 'absolute',
                                        width: '24px',
                                        height: '24px',
                                        border: '3px solid #e3f2fd', // ✅ Borde más grueso
                                        borderTop: '3px solid #0077b6',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }}></div>
                                    <div style={{
                                        position: 'absolute',
                                        width: '16px',
                                        height: '16px',
                                        top: '4px',
                                        left: '4px',
                                        border: '2px solid transparent',
                                        borderBottom: '2px solid #00b4d8',
                                        borderRadius: '50%',
                                        animation: 'spin 0.7s linear infinite reverse'
                                    }}></div>
                                </div>
                                <span style={{ 
                                    fontSize: '14px', // ✅ Font más grande
                                    fontWeight: '600', // ✅ Más bold
                                    color: '#0077b6',
                                    background: 'linear-gradient(45deg, #0077b6, #00b4d8)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent'
                                }}>
                                    🧠 Solbox AI analizando productos...
                                </span>
                            </div>
                            {/* BARRA DE PROGRESO FALSA PARA MÁS EFECTO */}
                            <div style={{
                                marginTop: '10px',
                                width: '60%',
                                height: '2px',
                                backgroundColor: '#e9ecef',
                                borderRadius: '1px',
                                margin: '10px auto 0',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: '30%',
                                    height: '100%',
                                    backgroundColor: '#0077b6',
                                    borderRadius: '1px',
                                    animation: 'progress 1.5s ease-in-out infinite'
                                }}></div>
                            </div>
                        </div>
                    )}

                    {/* ✅ HEADER CON POWERED BY AI - SIN CONTADOR */}
                    {!isLoadingAI && suggestions.length > 0 && (
                        <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#f8f9fa',
                            borderBottom: '1px solid #e9ecef',
                            fontSize: '11px',
                            color: '#6c757d',
                            fontWeight: '500',
                            textAlign: 'center',
                            fontStyle: 'italic'
                        }}>
                            🤖 Powered by Solbox IA
                        </div>
                    )}
                    
                    {/* Lista de sugerencias - SIN CAMBIOS */}
                    {!isLoadingAI && suggestions.map((suggestion, index) => (
                        <div
                            key={index}
                            style={{
                                padding: '12px 16px',
                                cursor: isSearching ? 'wait' : 'pointer',
                                borderBottom: index < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                fontSize: '14px',
                                color: '#212529',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                opacity: isSearching ? 0.6 : 1,
                                backgroundColor: 'white'
                            }}
                            onMouseDown={() => !isSearching && selectSuggestion(suggestion)}
                            onMouseEnter={(e) => {
                                if (!isSearching) {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                    e.currentTarget.style.color = '#0077b6';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSearching) {
                                    e.currentTarget.style.backgroundColor = 'white';
                                    e.currentTarget.style.color = '#212529';
                                }
                            }}
                        >
                            {/* Indicador Neural */}
                            <div style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: 'linear-gradient(45deg, #0077b6, #00b4d8)',
                                flexShrink: 0
                            }}></div>
                            
                            <span style={{ 
                                flex: 1, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap',
                                fontWeight: '400'
                            }}>
                                {suggestion}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* ✅ CSS PARA SPINNER */}
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @keyframes progress {
                    0% { transform: translateX(-100%); }
                    50% { transform: translateX(200%); }
                    100% { transform: translateX(-100%); }
                }
            `}</style>

            {/* Error styling */}
            {debugInfo && debugInfo.includes('Error') && (
                <div style={{
                    marginTop: '4px',
                    padding: '8px 12px',
                    backgroundColor: '#fff5f5',
                    border: '1px solid #fed7d7',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#c53030',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}>
                    <i className="fas fa-exclamation-triangle" style={{ fontSize: '10px' }}></i>
                    {debugInfo}
                </div>
            )}
        </div>
    );
};

export default BuscadorNuevo;